/**
 * Freshness tracker for the orders sync (feature 334, research D7). Pure and
 * clock-injectable. Shared by the kitchen screen and the waiter app: the copy
 * under kitchen/expo-app/src/lib/sync must stay byte for byte identical.
 *
 * The device clock is only used to show "data as of HH:MM" and the staleness
 * banner; it is never sent to the server as truth.
 */

export const STALE_AFTER_MS = 10_000;

export interface Freshness {
  readonly lastSuccessAt: number | null;
  markSuccess(now?: number): void;
  /** True when `now` is `thresholdMs` or more after the last success (or, before any success, after creation). */
  isStale(now?: number, thresholdMs?: number): boolean;
  formattedLastSuccess(): string;
}

/** HH:MM, 24 hours, device local time. `--:--` when there was no success yet. */
export function formatClock(timestamp: number | null): string {
  if (timestamp === null) return "--:--";
  const d = new Date(timestamp);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function createFreshness(clock: () => number = Date.now): Freshness {
  const startedAt = clock();
  let last: number | null = null;
  return {
    get lastSuccessAt() {
      return last;
    },
    markSuccess(now = clock()) {
      last = now;
    },
    isStale(now = clock(), thresholdMs = STALE_AFTER_MS) {
      return now - (last ?? startedAt) >= thresholdMs;
    },
    formattedLastSuccess() {
      return formatClock(last);
    },
  };
}
